// frontend/src/api/apiClient.js
import axios from 'axios'

// Axios 인스턴스를 생성하여 기본 설정
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 10000,
  withCredentials: true, // CORS 요청에 credential 포함
  headers: {
    'Content-Type': 'application/json',
  },
})

// 요청 인터셉터
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 응답 인터셉터: 응답 처리
apiClient.interceptors.response.use(
  response => {
    // 응답 헤더에서 새로운 만료 시간 확인
    const newExpiresAt = response.headers['x-session-expires']
    if (newExpiresAt) {
      const token = getToken()
      if (token) {
        // localStorage에 저장된 토큰인 경우에만 만료 시간 업데이트
        const storedToken = localStorage.getItem('token')
        if (storedToken === token) {
          localStorage.setItem('tokenExpiresAt', newExpiresAt)
        }
      }
    }
    return response
  },
  error => {
    console.error('API Error:', {
      config: error.config,
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
    })

    // 401 에러 처리 개선
    if (error.response?.status === 401) {
      // 토큰 제거
      removeToken()

      // 현재 페이지가 로그인 페이지가 아닐 경우에만 리다이렉트
      if (window.location.pathname !== '/login') {
        // router.push() 대신 직접 리다이렉트
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }
    return Promise.reject(error)
  }
)

export const authAPI = {
  login: (branchCode, password, rememberMe) => {
    return apiClient.post('/auth/login', {
      branch_code: branchCode,
      password: password,
      remember_me: rememberMe,
    })
  },
  logout: () => apiClient.post('/auth/logout'),
  getSessions: () => apiClient.get('/auth/sessions'),
  deleteSession: token => {
    console.log('세션 삭제 API 호출:', token)
    return apiClient.delete(`/auth/sessions/${encodeURIComponent(token)}`)
  },
  verifyBranchAndPhone: (branchCode, phoneNumber) => {
    return apiClient.post('/auth/verify-branch', {
      branch_code: branchCode,
      phone: phoneNumber,
    })
  },
  resetPassword: (branchCode, newPassword, verificationToken) => {
    return apiClient.post('/auth/reset-password', {
      branch_code: branchCode,
      new_password: newPassword,
      verification_token: verificationToken,
    })
  },
}

export function saveToken(token, rememberMe) {
  try {
    if (rememberMe) {
      localStorage.setItem('token', token)
      // 토큰 만료 시간 저장 (24시간)
      const expiresAt = new Date().getTime() + 24 * 60 * 60 * 1000
      localStorage.setItem('tokenExpiresAt', expiresAt.toString())
    } else {
      sessionStorage.setItem('token', token)
    }
  } catch (error) {
    console.error('토큰 저장 중 오류:', error)
  }
}

export function getToken() {
  try {
    // localStorage 토큰 확인
    const token = localStorage.getItem('token')
    const expiresAt = localStorage.getItem('tokenExpiresAt')

    if (token && expiresAt) {
      // 토큰 만료 체크
      if (new Date().getTime() > parseInt(expiresAt)) {
        removeToken()
        return null
      }
      return token
    }

    // sessionStorage 토큰 확인
    const sessionToken = sessionStorage.getItem('token')
    return sessionToken
  } catch (error) {
    return null
  }
}

export function removeToken() {
  localStorage.removeItem('token')
  localStorage.removeItem('tokenExpiresAt') // 만료 시간도 제거
  sessionStorage.removeItem('token')
}

export default apiClient
